import { useState, useEffect, useRef, useCallback } from 'react';

import MD5 from 'crypto-js/md5';
import { message } from 'antd';

import request from '@/request';

export default function ResetPwd({ initialPhone = '', onSuccess, onBack }) {
  const [step, setStep] = useState(1); // 1: 验证手机号, 2: 重置密码
  const [phone, setPhone] = useState(initialPhone);
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [countDown, setCountDown] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const resetCode = useRef('');

  const isStep1Valid = phone && smsCode;
  const isStep2Valid =
    newPassword && confirmPassword && newPassword === confirmPassword;

  const handleSendCode = useCallback(async () => {
    if (!phone) {
      return message.open({
        type: 'warning',
        content: '请先输入手机号',
      });
    }
    if (countDown > 0 || isSendingCode) return;

    setIsSendingCode(true);

    try {
      await request.post('/auth/sms/code', {
        template: 'SMS_327844250',
        phone: String(phone).trim(),
      });
      message.open({
        type: 'success',
        content: '发送成功',
      });
      // 发送成功后开始倒计时
      setCountDown(60);
    } finally {
      setIsSendingCode(false);
    }
  }, [phone, countDown, isSendingCode]);

  useEffect(() => {
    if (countDown <= 0) return;

    const t = setInterval(() => {
      setCountDown((s) => {
        if (s <= 1) {
          clearInterval(t);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [countDown]);

  const handleStep1Submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isStep1Valid) return;

      setIsLoading(true);

      try {
        const result = await request.post('/auth/sms/code/token ', {
          template: 'SMS_327844250',
          phone: String(phone).trim(),
          code: smsCode,
        });
        if (result) resetCode.current = result;
        setStep(2);
      } finally {
        setIsLoading(false);
      }
    },
    [isStep1Valid, phone, smsCode]
  );

  const handleStep2Submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isStep2Valid) return;

      if (newPassword !== confirmPassword) {
        return message.open({
          type: 'warning',
          content: '两次输入的密码不一致',
        });
      }

      setIsLoading(true);

      try {
        await request.post('/auth/password/forget', {
          code: resetCode.current,
          newPassword: MD5(String(newPassword).trim()).toString(),
        });
        message.open({
          type: 'success',
          content: '重置成功',
        });
        onSuccess?.();
      } finally {
        setIsLoading(false);
      }
    },
    [isStep2Valid, newPassword, confirmPassword, onSuccess]
  );

  return (
    <form
      onSubmit={step === 1 ? handleStep1Submit : handleStep2Submit}
      className="space-y-5"
    >
      {step === 1 ? (
        <>
          {/* Step 1: 手机号和验证码 */}
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-semibold text-slate-900 mb-3 text-left"
            >
              手机号
            </label>
            <div className="relative">
              <input
                id="phone"
                type="tel"
                placeholder="输入您的手机号"
                maxLength={11}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3 text-base text-slate-900 bg-slate-50 border-2 rounded-lg transition-all duration-200 placeholder:text-slate-400 ${
                  focusedField === 'phone'
                    ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10'
                    : 'border-slate-200 hover:border-slate-300'
                } focus:outline-none`}
                required
              />
              {focusedField === 'phone' && phone && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <svg
                    className="w-5 h-5 text-blue-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="sms"
              className="block text-sm font-semibold text-slate-900 mb-3 text-left"
            >
              短信验证码
            </label>
            <div className="relative flex gap-3">
              <input
                id="sms"
                type="text"
                inputMode="numeric"
                placeholder="输入短信验证码"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                onFocus={() => setFocusedField('sms')}
                onBlur={() => setFocusedField(null)}
                className={`flex-1 px-4 py-3 text-base text-slate-900 bg-slate-50 border-2 rounded-lg transition-all duration-200 placeholder:text-slate-400 ${
                  focusedField === 'sms'
                    ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10'
                    : 'border-slate-200 hover:border-slate-300'
                } focus:outline-none`}
                required
              />

              <button
                type="button"
                onClick={handleSendCode}
                disabled={countDown > 0 || isSendingCode}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap min-w-[100px] flex items-center justify-center gap-1 ${
                  countDown > 0 || isSendingCode
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {isSendingCode ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>发送中</span>
                  </>
                ) : countDown > 0 ? (
                  `${countDown}s`
                ) : (
                  '获取验证码'
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !isStep1Valid}
            className={`w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-2 text-base mt-8 ${
              isStep1Valid && !isLoading
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:shadow-md hover:shadow-blue-500/20 active:from-blue-700 active:to-blue-800'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>验证中...</span>
              </>
            ) : (
              <span>下一步</span>
            )}
          </button>
        </>
      ) : (
        <>
          {/* Step 2: 重置密码 */}
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-semibold text-slate-900 mb-3 text-left"
            >
              新密码
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type="password"
                placeholder="输入新密码"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onFocus={() => setFocusedField('newPassword')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3 text-base text-slate-900 bg-slate-50 border-2 rounded-lg transition-all duration-200 placeholder:text-slate-400 ${
                  focusedField === 'newPassword'
                    ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10'
                    : 'border-slate-200 hover:border-slate-300'
                } focus:outline-none`}
                required
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-semibold text-slate-900 mb-3 text-left"
            >
              确认密码
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type="password"
                placeholder="再次输入新密码"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3 text-base text-slate-900 bg-slate-50 border-2 rounded-lg transition-all duration-200 placeholder:text-slate-400 ${
                  focusedField === 'confirmPassword'
                    ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10'
                    : 'border-slate-200 hover:border-slate-300'
                } focus:outline-none`}
                required
              />
              {newPassword &&
                confirmPassword &&
                newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">
                    两次输入的密码不一致
                  </p>
                )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || !isStep2Valid}
            className={`w-full py-3 px-4 font-semibold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-2 text-base mt-8 ${
              isStep2Valid && !isLoading
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 hover:shadow-md hover:shadow-blue-500/20 active:from-blue-700 active:to-blue-800'
                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>重置中...</span>
              </>
            ) : (
              <span>重置密码</span>
            )}
          </button>
        </>
      )}

      {/* Back to Login Link */}
      {onBack && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-blue-600 hover:underline transition-colors"
          >
            返回登录
          </button>
        </div>
      )}
    </form>
  );
}
